// evaluation-planner.js -- Director's Season Plan generator.
import { DB } from './db.js';
import { planWindows } from './eval-window.js';
import { PRACTICE_BANDS, bandLabel } from './practice-data.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';

export const PLANNER = (() => {

  // Fixed pillar cycle -- matches the rubric's domains.
  const PILLAR_ORDER = ['Technical', 'Tactical', 'Physical', 'Mental', 'Social'];

  let _teams   = [];   // [{id, age_group, line}]
  let _plans   = [];   // [{id, name, season_start, season_end, cadence_days, pillar_order, team_exclusions}]
  let _planCfg = null; // current new-plan draft + expanded schedule preview

  // ── ENTRY ───────────────────────────────────────────────────────────────────

  async function show() {
    UI.showScreen('screen-evaluation-planner');
    UI.setLoading('planner-content', 'Loading plans...');
    try {
      const [teams, plans] = await Promise.all([
        DB.teams.getAll(),
        DB.evaluationPlans.list(),
      ]);
      _teams = teams || [];
      _plans = plans || [];
      _renderList();
    } catch (err) {
      console.error('Planner load failed:', err);
      UI.setEmpty('planner-content', 'Could not load season plans.');
    }
  }

  // ── PLAN LIST ───────────────────────────────────────────────────────────────

  function _renderList() {
    const esc = UTILS.escapeHtml;
    let html = `
      <div class="planner-toolbar">
        <button class="btn btn--primary" id="planner-new-btn">+ New plan</button>
      </div>`;
    if (!_plans.length) {
      html += `
        <div class="empty-state">
          <p class="empty-state__message">No season plans yet.<br>
          One stable code per coach &mdash; the plan tells the app which pillar is open today.</p>
        </div>`;
    } else {
      html += '<div class="planner-list">';
      _plans.forEach(p => {
        const pillars = _parseJson(p.pillar_order, []);
        // Breaks pause the rotation (indoor season etc.) - show them, so it is
        // obvious why no window opens during those weeks.
        const breaks = _parseJson(p.breaks, []);
        html += `
          <button class="planner-card" data-plan="${esc(p.id)}">
            <div class="planner-card__title">${esc(p.name)}</div>
            <div class="planner-card__sub">
              ${esc(p.season_start)} &rarr; ${esc(p.season_end)}
              &middot; every ${p.cadence_days}d
              &middot; ${pillars.join(' &rarr; ') || 'cycle'}
            </div>
            ${breaks.length ? `<div class="planner-card__sub">Paused: ${
              breaks.map(b => `${esc(b.from)} &rarr; ${esc(b.to)}`).join(', ')}</div>` : ''}
          </button>`;
      });
      html += '</div>';
    }
    document.getElementById('planner-content').innerHTML = html;
    document.getElementById('planner-new-btn')?.addEventListener('click', _showNewForm);
    document.querySelectorAll('[data-plan]').forEach(b => {
      b.addEventListener('click', () => _showPlanDetail(b.dataset.plan));
    });
  }

  // ── NEW PLAN FORM ───────────────────────────────────────────────────────────

  function _showNewForm() {
    const esc = UTILS.escapeHtml;
    const today = new Date().toISOString().slice(0, 10);
    const cfg = _planCfg || {};
    const exc = (cfg.exclusions || {});

    let html = `
      <div class="planner-form">
        <label class="form-label">Plan name</label>
        <input type="text" id="plan-name" class="form-input"
               placeholder="e.g. 2026/27 season"
               value="${esc(cfg.name || '')}" />

        <label class="form-label">Season start (first game)</label>
        <input type="date" id="plan-start" class="form-input"
               value="${esc(cfg.start || today)}" />
        <p class="planner-hint">Evaluations count from here.</p>

        <label class="form-label">Training start</label>
        <p class="planner-hint">When coaches are back on the pitch, if that is before the first game. The season plan's weekly practice counts from here, so the pre-season block lands in the weeks it was written for. The ages rarely start together, so set the club default and override any age that differs. Leave blank to use the season start.</p>
        <input type="date" id="plan-training-default" class="form-input"
               value="${esc((cfg.trainingStarts || {}).default || '')}"
               aria-label="Training start for the whole club" />
        <div class="planner-training-starts">
          ${PRACTICE_BANDS.map(b => `
            <label class="planner-training-start">
              <span>${esc(bandLabel(b))}</span>
              <input type="date" data-training-band="${esc(b)}" class="form-input"
                     value="${esc((cfg.trainingStarts || {})[b] || '')}" />
            </label>`).join('')}
        </div>

        <label class="form-label">Season end</label>
        <input type="date" id="plan-end" class="form-input"
               value="${esc(cfg.end || '')}" />

        <label class="form-label">Cadence (days between pillar changes)</label>
        <input type="number" id="plan-cadence" class="form-input"
               min="7" max="60" value="${esc(cfg.cadence || 14)}" />

        <label class="form-label">Pillar cycle</label>
        <div class="planner-pillars">
          ${PILLAR_ORDER.map(p => `<span class="planner-pill">${esc(p)}</span>`).join(' &rarr; ')}
        </div>
        <p class="planner-hint">Repeats in this fixed order from season start to season end.</p>

        <label class="form-label">Per-team pillar exclusions</label>
        <p class="planner-hint">Tap a pillar chip to exclude it for that team. Excluded pillars give that team "no work this week" when that pillar's window is open.</p>
        <div class="planner-exclusion-grid">`;
    _teams.forEach(t => {
      const skip = exc[t.id] instanceof Set ? exc[t.id] : new Set(exc[t.id] || []);
      // Lead with the team code (unique): several teams share an age group, so
      // labelling by age group alone made every row read identically.
      const teamLabel = t.id + '  ·  ' + (t.age_group || '') + (t.line ? ' · ' + t.line : '');
      html += `
        <div class="planner-exclusion-row">
          <div class="planner-exclusion-team">${esc(teamLabel)}</div>
          <div class="planner-exclusion-pillars">
            ${PILLAR_ORDER.map(p => `
              <button class="planner-team-chip ${skip.has(p) ? 'planner-team-chip--off' : ''}"
                      data-team="${esc(t.id)}" data-pillar="${esc(p)}">${esc(p)}</button>
            `).join('')}
          </div>
        </div>`;
    });
    html += `</div>

        <div class="planner-form__actions">
          <button class="btn btn--secondary" id="planner-cancel">Cancel</button>
          <button class="btn btn--primary" id="planner-preview-btn">Preview &rarr;</button>
        </div>
      </div>`;
    document.getElementById('planner-content').innerHTML = html;

    // Re-hydrate _planCfg.exclusions as Sets so chip clicks mutate cleanly.
    _planCfg = _planCfg || {};
    _planCfg.exclusions = {};
    _teams.forEach(t => {
      const existing = exc[t.id];
      _planCfg.exclusions[t.id] = existing instanceof Set ? existing : new Set(existing || []);
    });

    document.getElementById('planner-cancel').addEventListener('click', () => {
      _planCfg = null;
      _renderList();
    });
    document.getElementById('planner-preview-btn').addEventListener('click', _showPreview);
    document.querySelectorAll('[data-team][data-pillar]').forEach(btn => {
      btn.addEventListener('click', () => {
        const tid = btn.dataset.team;
        const p   = btn.dataset.pillar;
        if (!_planCfg.exclusions[tid]) _planCfg.exclusions[tid] = new Set();
        if (_planCfg.exclusions[tid].has(p)) _planCfg.exclusions[tid].delete(p);
        else                                  _planCfg.exclusions[tid].add(p);
        btn.classList.toggle('planner-team-chip--off');
      });
    });
  }

  function _readForm() {
    const name    = document.getElementById('plan-name').value.trim();
    const start   = document.getElementById('plan-start').value;
    const end     = document.getElementById('plan-end').value;
    const cadence = parseInt(document.getElementById('plan-cadence').value, 10);
    if (!name)                                   return { error: 'Please enter a plan name.' };
    if (!start || !end)                          return { error: 'Please pick start and end dates.' };
    if (new Date(end) <= new Date(start))        return { error: 'End date must be after the start date.' };
    if (!cadence || cadence < 7 || cadence > 60) return { error: 'Cadence must be between 7 and 60 days.' };

    // Training starts: the club default plus any age that differs. Blank fields
    // are simply left out, so the map only ever holds real decisions.
    const trainingStarts = {};
    const dflt = document.getElementById('plan-training-default')?.value || '';
    if (dflt) trainingStarts.default = dflt;
    document.querySelectorAll('[data-training-band]').forEach(el => {
      if (el.value) trainingStarts[el.dataset.trainingBand] = el.value;
    });
    // Training may start before the first game, never after it: the point of
    // these dates is the pre-season weeks, and a later one would leave a gap
    // where coaches are training with no practice to show them.
    for (const [k, v] of Object.entries(trainingStarts)) {
      const who = k === 'default' ? 'The training start' : `The ${bandLabel(k)} training start`;
      if (new Date(v) > new Date(start)) {
        return { error: `${who} must be on or before the season start.` };
      }
      if (new Date(v) >= new Date(end)) {
        return { error: `${who} must be before the season ends.` };
      }
    }
    return { name, start, trainingStarts, end, cadence };
  }

  function _expandWindows(start, end, cadenceDays) {
    const DAY = 86400000;
    const startMs = new Date(start).getTime();
    const endMs   = new Date(end).getTime();
    const windows = [];
    let openMs = startMs;
    let i = 0;
    while (openMs <= endMs) {
      const closeMs = Math.min(openMs + (cadenceDays * DAY) - DAY, endMs);
      windows.push({
        index:     i,
        pillar:    PILLAR_ORDER[i % PILLAR_ORDER.length],
        validFrom: new Date(openMs).toISOString().slice(0, 10),
        expiresAt: new Date(closeMs).toISOString().slice(0, 10),
      });
      i++;
      openMs += cadenceDays * DAY;
    }
    return windows;
  }

  // ── PREVIEW ─────────────────────────────────────────────────────────────────

  function _showPreview() {
    const cfg = _readForm();
    if (cfg.error) { UI.toast(cfg.error, 'error'); return; }
    cfg.windows    = _expandWindows(cfg.start, cfg.end, cfg.cadence);
    cfg.exclusions = (_planCfg && _planCfg.exclusions) || {};
    _planCfg = Object.assign(_planCfg || {}, cfg);
    _renderPreview();
  }

  function _renderPreview() {
    const esc = UTILS.escapeHtml;
    const cfg = _planCfg;
    const wins = cfg.windows;
    const shown = wins.slice(0, 8);

    let html = `
      <div class="planner-preview-head">
        <strong>${esc(cfg.name)}</strong>
        <span class="planner-hint">
          Defines the fortnightly pillar rotation. Assign this plan to a coach on the Staff screen; the plan then picks today's pillar automatically for that coach's team.
        </span>
      </div>

      <div class="planner-window">
        <div class="planner-window__head"><strong>Schedule (${wins.length} windows)</strong></div>
        <div class="planner-schedule-list">`;
    shown.forEach(w => {
      html += `
        <div class="planner-schedule-item">
          <span><strong>${esc(w.pillar)}</strong></span>
          <span class="planner-hint">${esc(w.validFrom)} &rarr; ${esc(w.expiresAt)}</span>
        </div>`;
    });
    if (wins.length > shown.length) {
      html += `<div class="planner-schedule-item planner-hint">... +${wins.length - shown.length} more</div>`;
    }
    html += `</div></div>

      <div class="planner-window">
        <div class="planner-window__head"><strong>Per-team preview (${_teams.length} teams)</strong></div>
        <div class="planner-schedule-list">`;
    _teams.forEach(t => {
      const teamLabel = t.id + '  ·  ' + (t.age_group || '') + (t.line ? ' · ' + t.line : '');
      const skip = cfg.exclusions[t.id];
      const skipArr = skip instanceof Set ? [...skip] : (skip || []);
      const skipText = skipArr.length ? `skips ${skipArr.join(', ')}` : 'evaluates every pillar';
      html += `
        <div class="planner-schedule-item">
          <span>${esc(teamLabel)}</span>
          <span class="planner-hint">${esc(skipText)}</span>
        </div>`;
    });
    html += `</div></div>

      <div class="planner-form__actions">
        <button class="btn btn--secondary" id="planner-back">&larr; Edit plan</button>
        <button class="btn btn--primary" id="planner-generate-btn">Save plan</button>
      </div>`;
    document.getElementById('planner-content').innerHTML = html;
    document.getElementById('planner-back').addEventListener('click', _showNewForm);
    document.getElementById('planner-generate-btn').addEventListener('click', _generate);
  }

  // ── GENERATE ────────────────────────────────────────────────────────────────

  async function _generate() {
    const cfg = _planCfg;
    if (!cfg) return;
    const btn = document.getElementById('planner-generate-btn');
    if (btn) { btn.disabled = true; btn.textContent = 'Saving...'; }
    try {
      // Sets -> plain arrays for storage.
      const exclusions = {};
      Object.keys(cfg.exclusions || {}).forEach(tid => {
        const s = cfg.exclusions[tid];
        const arr = s instanceof Set ? [...s] : (s || []);
        if (arr.length) exclusions[tid] = arr;
      });

      const planRows = await DB.evaluationPlans.create({
        name:            cfg.name,
        season_start:    cfg.start,
        training_starts: cfg.trainingStarts || {},
        season_end:      cfg.end,
        cadence_days:    cfg.cadence,
        pillar_order:    PILLAR_ORDER,
        team_exclusions: exclusions,
      });
      const plan   = Array.isArray(planRows) ? planRows[0] : planRows;
      const planId = plan?.id;
      if (!planId) throw new Error('Plan row missing id');

      // The plan is DEFINED here only. Coaches pick it up through their team
      // assignment on the Staff screen (the ST staff-code model). No per-team
      // login codes are generated any more - that was the retired team-code model.
      void planId;

      UI.toast('Plan saved. Assign it to coaches on the Staff screen.', 'success', 3000);
      _planCfg = null;
      await show();
    } catch (err) {
      console.error('Plan generation failed:', err);
      UI.toast('Could not generate plan', 'error');
      if (btn) { btn.disabled = false; btn.textContent = `Create plan + ${_teams.length} codes`; }
    }
  }

  // ── PLAN DETAIL ─────────────────────────────────────────────────────────────

  function _showPlanDetail(planId) {
    // Everything on this screen comes off the plan row itself. It used to fetch
    // the per-window login codes and build the schedule out of them; those codes
    // stopped being generated when coaches moved to personal staff codes, so the
    // screen quietly went blank and showed "0 codes / legacy plan" instead of
    // the season. The schedule was always derivable from the plan.
    _renderPlanDetail(_plans.find(p => p.id === planId));
  }

  function _renderPlanDetail(plan) {
    const esc   = UTILS.escapeHtml;
    const today = UTILS.today();
    const wins  = planWindows(plan, null, today);
    const open  = wins.find(w => w.status === 'open');

    // How many evaluation windows each pillar actually gets. The rotation is
    // even only if the season divides cleanly by cadence times pillars, which
    // it rarely does, so a pillar can quietly end up with half the coverage of
    // another. Better the director sees that here than discovers it in June.
    const tally = {};
    wins.filter(w => w.reason === 'open')
        .forEach(w => { tally[w.pillar] = (tally[w.pillar] || 0) + 1; });
    const thin = Object.keys(tally).length
      ? Math.min(...Object.values(tally)) < Math.max(...Object.values(tally))
      : false;

    const nowLine =
        open && open.reason === 'open' ? `Open now: <strong>${esc(open.pillar)}</strong>, until ${esc(open.to)}`
      : open && open.reason === 'break' ? `Paused for the break until ${esc(open.to)}`
      : today < (plan?.season_start || '') ? `Season starts ${esc(plan?.season_start || '')}`
      : today > (plan?.season_end || '')   ? 'Season finished'
      : 'Nothing open today';

    let html = `
      <div class="planner-detail-head">
        <button class="btn btn--secondary" id="planner-back-list">&larr; Back</button>
        <div class="planner-detail-title">
          <strong>${esc(plan?.name || 'Plan')}</strong>
          <span class="planner-hint">${esc(plan?.season_start || '')} &rarr; ${esc(plan?.season_end || '')} &middot; every ${esc(String(plan?.cadence_days || 14))} days</span>
        </div>
        <button class="btn btn--secondary" id="planner-csv-btn">Export CSV</button>
        <button class="btn btn--danger"    id="planner-delete-btn">Delete plan</button>
      </div>
      <p class="planner-hint">${nowLine}</p>`;

    if (thin) {
      const parts = Object.keys(tally).map(k => `${esc(k)} ${tally[k]}`).join(', ');
      html += `<p class="planner-hint">Windows per pillar: ${parts}. Adjust the end date or the order if that is not what you want.</p>`;
    }

    if (!wins.length) {
      html += '<p class="planner-hint">This plan has no dates, so there is nothing to schedule.</p>';
    } else {
      html += '<div class="planner-windows">';
      let n = 0;
      wins.forEach(w => {
        const paused = w.reason !== 'open';
        if (!paused) n++;
        const title = paused
          ? (w.reason === 'break' ? 'Break, rotation paused' : 'Nothing scheduled')
          : `${n}. ${w.pillar}`;
        // A break is never "upcoming": nothing opens, so borrowing the window
        // vocabulary for it reads as another evaluation slot.
        const label = paused ? 'Paused'
                    : w.status === 'open' ? 'Open now'
                    : w.status === 'past' ? 'Closed'
                    : 'Upcoming';
        html += `
          <div class="planner-window planner-window--${esc(paused ? 'past' : w.status)}">
            <div class="planner-window__head">
              <strong>${esc(title)}</strong>
              <span class="planner-window__dates">${esc(w.from)} &rarr; ${esc(w.to)} &middot; ${w.days}d</span>
              <span class="planner-window__status planner-window__status--${esc(paused ? 'past' : w.status)}">${esc(label)}</span>
            </div>
          </div>`;
      });
      html += '</div>';
    }

    document.getElementById('planner-content').innerHTML = html;
    document.getElementById('planner-back-list').addEventListener('click', _renderList);
    document.getElementById('planner-csv-btn').addEventListener('click', () => _exportCsv(plan, wins));
    document.getElementById('planner-delete-btn').addEventListener('click', () => _deletePlan(plan));
  }

  function _deletePlan(plan) {
    if (!plan) return;
    UI.modal({
      title: `Delete "${plan.name}"?`,
      body:  'The plan row is deleted; codes generated by it stay in the database (their plan link clears). To stop a code from working, revoke it from the regular code list.',
      buttons: [
        { label: 'Delete plan', style: 'primary', action: async () => {
          try {
            await DB.evaluationPlans.delete(plan.id);
            UI.toast('Plan deleted', 'success', 1500);
            await show();
          } catch (err) {
            console.error('Plan delete failed:', err);
            UI.toast('Delete failed', 'error');
          }
        } },
        { label: 'Cancel' },
      ],
    });
  }

  function _exportCsv(plan, wins) {
    const rows = [['Window', 'Pillar', 'Opens', 'Closes', 'Days', 'Status']];
    let n = 0;
    (wins || []).forEach(w => {
      const paused = w.reason !== 'open';
      if (!paused) n++;
      rows.push([
        paused ? '' : n,
        paused ? (w.reason === 'break' ? 'BREAK' : w.reason.toUpperCase()) : w.pillar,
        w.from, w.to, w.days,
        w.status === 'open' ? 'Open now' : w.status === 'past' ? 'Closed' : 'Upcoming',
      ]);
    });
    const slug = (plan?.name || 'plan').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    UTILS.downloadCsv(rows, `season-plan_${slug}.csv`, { delimiter: ',' });
  }

  // ── HELPERS ─────────────────────────────────────────────────────────────────

  function _parseJson(value, fallback) {
    if (Array.isArray(value)) return value;
    if (!value) return fallback;
    try { const v = JSON.parse(value); return Array.isArray(v) ? v : fallback; }
    catch { return fallback; }
  }

  return { show };
})();
